import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Footer, ThemeToggle, Wordmark } from "@/components/Layout";
import { Button } from "@/components/ui/button";

export default function OAuthConsent() {
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);

  const decide = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post("/oauth/authorize/approve", {
        client_id: params.get("client_id") || "",
        redirect_uri: params.get("redirect_uri") || "",
        state: params.get("state") || "",
        scope: params.get("scope") || "memories:read",
        code_challenge: params.get("code_challenge") || "",
        code_challenge_method: params.get("code_challenge_method") || "S256",
        action,
      });
      window.location.href = data.redirect;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        <Wordmark />
        <ThemeToggle />
      </header>
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 pb-20 sm:px-6">
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm" data-testid="oauth-consent-card">
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 text-muted-foreground" />
            <h1 className="font-display text-2xl font-medium tracking-tight">Connect MIA to ChatGPT</h1>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            An external application is requesting access to your MIA account.
          </p>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground" data-testid="oauth-scope-list">
            <li>— Read-only access to your captured memories</li>
            <li>— Only your own memories are ever visible</li>
            <li>— No writes, no deletions, no device access</li>
          </ul>
          <p className="mt-4 font-mono text-xs text-muted-foreground" data-testid="oauth-client-id">
            client: {params.get("client_id") || "unknown"}
          </p>
          <div className="mt-8 flex gap-3">
            <Button variant="outline" className="flex-1" data-testid="oauth-deny-button"
                    onClick={() => decide("deny")} disabled={busy}>
              Deny
            </Button>
            <Button className="flex-1" data-testid="oauth-approve-button"
                    onClick={() => decide("approve")} disabled={busy}>
              {busy ? "Connecting…" : "Approve"}
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
